// Time-of-day buckets, resolved against each station's LOCAL SOLAR time so a
// single frame shows e.g. local noon at every location simultaneously ("sun
// overhead everywhere"), not one UTC instant.
//
// Local solar time is derived purely from longitude (15 deg = 1 hour), which
// keeps the whole pipeline dependency-free and matches the intuitive meaning of
// "midday". (A real timezone/DST database would give civil wall-clock time
// instead; swap-in point noted in build.js.)

export interface Bucket {
  key: string;
  label: string;
  targetHour: number;
  emoji: string;
}

export const BUCKETS: Bucket[] = [
  { key: 'morning', label: 'Morning', targetHour: 6, emoji: '🌅' }, // 06:00
  { key: 'lunch', label: 'Midday', targetHour: 12, emoji: '☀️' }, // 12:00
  { key: 'evening', label: 'Evening', targetHour: 18, emoji: '🌇' }, // 18:00
  { key: 'night', label: 'Night', targetHour: 0, emoji: '🌙' }, // 00:00
];

export const BUCKET_KEYS = BUCKETS.map((b) => b.key);

// The time of day a reader gets before choosing one: midday, because "what is it
// like there" is a question about the day, not about dawn.
//
// One definition, because two things answer it independently and must agree — the
// client's initial state, and the page pre-render, which bakes a briefing for
// whichever bucket the reader will be shown. Baked at the wrong one, a page
// states morning's readings to a crawler and to anyone without JS, and midday's
// to everyone else, with nothing to indicate which is which. It is NOT
// manifest.buckets[0]: that is an ordering of the day, and it starts at dawn.
export const DEFAULT_BUCKET = 'lunch';

// Half-width (hours) of the window around a target we accept. Targets are 6h
// apart, so 3h guarantees every observation lands in exactly one bucket; with
// typically-hourly data the chosen obs is almost always within ~1h of target.
export const BUCKET_WINDOW_H = 3;

// Circular distance in hours between two hours-of-day (0..24).
export function hourDistance(a: number, b: number): number {
  let d = Math.abs(a - b) % 24;
  if (d > 12) d = 24 - d;
  return d;
}

// Given a local hour (0..24), return the index of the nearest bucket, or -1 if
// somehow outside every window (shouldn't happen with 6h spacing).
export function bucketForHour(localHour: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < BUCKETS.length; i++) {
    const d = hourDistance(localHour, BUCKETS[i].targetHour);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return bestD <= BUCKET_WINDOW_H ? best : -1;
}

// Convert a UTC epoch-ms + longitude into local-solar wall-clock components.
// Returns { y, m, d, hour } where hour is fractional (0..24) and y/m/d are the
// LOCAL calendar date (may differ from the UTC date near the date line).
export function localSolar(utcMs: number, lon: number): { y: number; m: number; d: number; hour: number } {
  const localMs = utcMs + (lon / 15) * 3600 * 1000;
  const ld = new Date(localMs);
  return {
    y: ld.getUTCFullYear(),
    m: ld.getUTCMonth() + 1,
    d: ld.getUTCDate(),
    hour: ld.getUTCHours() + ld.getUTCMinutes() / 60 + ld.getUTCSeconds() / 3600,
  };
}

// Zero-padded YYYY-MM-DD for a local date.
export function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
